import {getTenantLogo} from 'common/HelperFunctions'
import {GENERAL_ENABLE_DISABLE} from 'common/constants'
import {jsPDF, jsPDFOptions} from 'jspdf'
import autoTable, {Styles} from 'jspdf-autotable'
import store from 'setup/redux/Store'

import {getCSSVariableValue} from '_metronic/assets/ts/_utils'

import {EXPORT_COL} from './attendanceExportBuilder'
import {AttendanceReportExportPayload} from './attendanceReportExportTypes'

/** Meta lines the PDF leaves out. "Attendance Mode" names what a single mark covers ("Daily
 *  attendance"), which beside a date range reads as a claim about the reporting window; "Period"
 *  states a window in words that the answer's own title and rows already establish. Both are still
 *  written to the CSV export, which has no other header to carry them. */
const HIDDEN_PDF_META: string[] = [EXPORT_COL.PERIOD, EXPORT_COL.ATTENDANCE_MODE]

const colorToHex = (color: string): string => {
  const div = document.createElement('div')
  document.body.appendChild(div)
  div.style.color = color
  const computedColor = window.getComputedStyle(div).color
  document.body.removeChild(div)

  if (computedColor === '') {
    return '#333333'
  }

  const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
  if (!rgbMatch) {
    return '#333333'
  }

  const [, r, g, b] = rgbMatch.map(Number)
  const toHex = (n: number): string => n.toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

async function addTenantBranding(doc: jsPDF): Promise<number> {
  const generalSettings = store.getState()?.mainReducer?.generalSettings ?? {}
  let startY = 14

  const url = getTenantLogo('small', 'light')
  if (generalSettings?.hide_logo_reports === GENERAL_ENABLE_DISABLE.DISABLE && url) {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    const logoLoaded = await new Promise<boolean>((resolve) => {
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = `${url}?t=${Date.now()}`
    })

    if (logoLoaded) {
      const aspectRatio = img.width / img.height
      const maxWidth = 50
      const maxHeight = 20
      let finalWidth = maxWidth
      let finalHeight = finalWidth / aspectRatio
      if (finalHeight > maxHeight) {
        finalHeight = maxHeight
        finalWidth = finalHeight * aspectRatio
      }

      let imageData: string | HTMLImageElement = img
      if (url.toLowerCase().endsWith('.svg')) {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img, 0, 0)
          imageData = canvas.toDataURL('image/png')
        }
      }

      doc.addImage(imageData, 'PNG', 14, 2, finalWidth, finalHeight)
      startY = Math.max(startY, finalHeight + 8)
    }
  }

  if (generalSettings?.tenantName) {
    const pageWidth = doc.internal.pageSize.getWidth()
    const textWidth = doc.getTextWidth(generalSettings.tenantName)
    doc.text(generalSettings.tenantName, pageWidth - textWidth - 28, 10)
  }

  return startY
}

function computeColumnWidths(columns: string[], rows: Record<string, string>[]): number[] {
  return columns.map((col) => {
    const headerWidth = col.length * 2.5
    const maxDataLength = Math.max(0, ...rows.map((row) => String(row[col] ?? '').length))
    const dataWidth = maxDataLength * 2
    return Math.min(Math.max(headerWidth, dataWidth, 28), 70)
  })
}

export async function downloadAttendanceReportPdf(
  payload: AttendanceReportExportPayload
): Promise<void> {
  const {
    title,
    metaLines = [],
    summaryLines = [],
    chartImageDataUrl,
    columns,
    rows,
    filename,
  } = payload
  if (!columns.length || !rows.length) return

  const documentOptions: jsPDFOptions = {format: 'a4'}
  if (columns.length > 5) {
    documentOptions.orientation = 'landscape'
  }

  const doc = new jsPDF(documentOptions)
  const pageWidth = doc.internal.pageSize.getWidth()

  let y = await addTenantBranding(doc)
  y = Math.max(y, 28)

  // Everything below is written at a fixed left margin or centred on its own measured width, with
  // nothing bounding it on the right — a long student name or summary line ran straight off the
  // page. Wrap to the printable width instead.
  const textWidth = pageWidth - 28
  const writeWrapped = (text: string, lineHeight: number, centered = false) => {
    for (const line of doc.splitTextToSize(text, textWidth) as string[]) {
      doc.text(line, centered ? (pageWidth - doc.getTextWidth(line)) / 2 : 14, y)
      y += lineHeight
    }
  }

  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  writeWrapped(title, 10, true)

  doc.setFontSize(10)
  doc.setFont('helvetica', 'normal')

  for (const line of metaLines) {
    if (!line.trim()) continue
    if (HIDDEN_PDF_META.some((label) => line.startsWith(`${label}:`))) continue
    writeWrapped(line, 5)
  }

  if (summaryLines.length > 0) {
    y += 2
    doc.setFont('helvetica', 'bold')
    for (const line of summaryLines) {
      if (!line.trim()) continue
      writeWrapped(line, 5)
    }
    doc.setFont('helvetica', 'normal')
  }

  y += 4

  if (chartImageDataUrl) {
    try {
      const chartW = Math.min(170, pageWidth - 28)
      // Derive the height from the bitmap instead of hardcoding 40mm. The canvas is 500×120
      // (aspect 4.167) and the old box was 170×40 (4.25), so jsPDF stretched it to fit and the
      // donut printed as an ellipse — and the box no longer matches at all now that the canvas
      // grows with the number of status bars.
      const {width: imgW, height: imgH} = doc.getImageProperties(chartImageDataUrl)
      const chartH = (chartW * imgH) / imgW
      const chartX = (pageWidth - chartW) / 2
      // Nothing checked the chart against the page before: with a long identity block it was drawn
      // partly or wholly past the bottom edge.
      if (y + chartH > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage()
        y = 20
      }
      doc.addImage(chartImageDataUrl, 'PNG', chartX, y, chartW, chartH)
      y += chartH + 8
    } catch {
      // Chart is optional — continue with table if image fails
    }
  }

  const columnWidths = computeColumnWidths(columns, rows)
  const headers = [columns]
  const tableData = rows.map((row) => columns.map((col) => row[col] ?? ''))

  const totalWidth = columnWidths.reduce((acc, width) => acc + width, 0)
  const printableWidth = doc.internal.pageSize.getWidth() - 40
  const scalingFactor = totalWidth > printableWidth ? printableWidth / totalWidth : 1
  const scaledTotalWidth = columnWidths.reduce((acc, width) => acc + width * scalingFactor, 0)
  const centeredLeft = Math.max(14, (pageWidth - scaledTotalWidth) / 2)

  autoTable(doc, {
    head: headers,
    body: tableData,
    startY: y,
    theme: 'grid',
    styles: {
      fontSize: 10 * scalingFactor,
      cellPadding: 3,
      minCellHeight: 10,
      halign: 'center',
      valign: 'middle',
    },
    headStyles: {
      fillColor: colorToHex(getCSSVariableValue('--bs-primary-bg-subtle')),
      textColor: colorToHex(getCSSVariableValue('--bs-dark')),
    },
    columnStyles: columnWidths.reduce(
      (styles, width, index) => {
        styles[index] = {
          cellWidth: width * scalingFactor,
          overflow: 'linebreak',
          valign: 'middle',
        } as Partial<Styles>
        return styles
      },
      {} as Record<number, Partial<Styles>>
    ),
    margin: {top: 20, left: centeredLeft, right: centeredLeft, bottom: 20},
  })

  const safeName = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
  doc.save(safeName)
}
